import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as moment from 'moment-timezone';
import { Store } from '@ngrx/store';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { appDataDetails } from '../../../../../../../../shared/core/shared.selectors';
import { SampleRecordsService } from '../sample-records.service';
import {
  CreateOrEditSampleRecordComponent,
} from '../create-or-edit-sample-record/create-or-edit-sample-record.component';
import { Ability, subject } from '@casl/ability';

/**
 * Sample-records list (project-scoped). Mirrors the RFI list visual
 * conventions: search pill + "Create" in the header row, row-click opens
 * the side panel, paginated client-side. Sample collection has no workflow
 * status — the only badge is "Pushed" once an outbound sync to
 * AASHTOWare has stamped a `source_entity_id` onto the row.
 *
 * Access is ability-driven: "Create" needs SampleRecord create on the
 * project, and the panel decides read-only vs edit the same way. Under org
 * web read-only mode (Alaska DOT) every web user is a project owner, so the
 * tab is a viewer there.
 */
@Component({
  selector: 'app-sample-records-list',
  templateUrl: './sample-records-list.component.html',
  styleUrls: ['./sample-records-list.component.scss'],
  standalone: false,
})
export class SampleRecordsListComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'name',
    'labControlNumber',
    'fieldNumber',
    'material',
    'sampleType',
    'sampleDate',
    'source',
  ];
  dataSource: any[] = [];
  /** Every record the api returned, newest first. */
  originalDataSource: any[] = [];
  /** `originalDataSource` narrowed by the search pill; the pager slices this. */
  filteredRecords: any[] = [];
  searchForm = new FormGroup({ searchText: new FormControl('') });
  selectedRow: any;
  projectId: string;
  canCreate = false;
  projectTimezone: string;
  loading = false;
  loadError: string | null = null;

  totalRecords = 0;
  currentPage = 1;
  itemsPerPage = 10;

  /**
   * Deep link from global search: `?sampleId=` (the existing param rowClicked
   * writes) auto-opens the sample's details panel on load, one-shot, the same
   * way a row click would.
   */
  private pendingDeepLinkSampleId: string = null;

  private readonly onDestroy = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private sampleRecordsService: SampleRecordsService,
    private ability: Ability,
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
      this.refreshAbilities();
    }

    this.pendingDeepLinkSampleId = this.route.snapshot.queryParamMap.get('sampleId');

    this.fetchSampleRecords();

    // Client-side search over the identifying columns (same feel as the RFI
    // list). Debounced so typing doesn't re-slice on every keystroke.
    this.searchForm.controls.searchText.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(() => this.paginateData(true));

    // Abilities are replaced once /appdata loads (after login), so re-read
    // the create grant on every emission rather than only at init.
    this.store
      .select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.refreshAbilities());

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedRow = null));

    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.timezone) {
          this.projectTimezone = data.timezone;
        }
      });

    // Refresh after create/update/delete/push from the side panel.
    this.sampleRecordsService.refresh
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetchSampleRecords());
  }

  private refreshAbilities(): void {
    if (!this.projectId) return;
    this.canCreate = this.ability.can('create', subject('SampleRecord', { project_id: this.projectId }));
  }

  clearSearch(): void {
    this.searchForm.controls.searchText.setValue('');
  }

  /** Search across the columns the table shows plus the trailer/lab numbers. */
  private matchesSearch(row: any, needle: string): boolean {
    if (!needle) return true;
    const haystack = [
      row?.name,
      row?.lab_control_number,
      row?.control_number,
      row?.regional_lab_number,
      row?.material_name,
      row?.sample_type,
      row?.source_value,
      row?.sampler_value,
    ]
      .filter(v => v !== null && v !== undefined && v !== '')
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  }

  fetchSampleRecords(): void {
    if (!this.projectId) return;
    this.loading = true;
    this.loadError = null;
    this.sampleRecordsService
      .list({ projectId: this.projectId, start: 0, total: 1000 })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          // Endpoint returns `{ status, type, data: [...], full_count }`.
          const list = Array.isArray(resp?.data) ? resp.data : [];
          this.originalDataSource = list;

          // Each response rebuilds the row objects, so re-point the reference
          // the highlighted-row styling compares against.
          if (this.selectedRow?.uuid) {
            const current = list.find(row => row?.uuid === this.selectedRow.uuid);
            if (current) {
              this.selectedRow = current;
            }
          }

          this.paginateData(true);
          this.loading = false;
          this.tryOpenDeepLinkedSample();
        },
        error: err => {
          this.loading = false;
          // Org without sample_record_general config 404s — surface but don't crash.
          this.loadError = err?.error?.message || err?.message || 'Failed to load sample records';
          this.originalDataSource = [];
          this.filteredRecords = [];
          this.dataSource = [];
          this.totalRecords = 0;
        },
      });
  }

  /**
   * Auto-opens the details panel for a deep-linked sample (?sampleId=) once
   * the list has loaded, reusing the row-click data path (the panel refetches
   * the full record by uuid itself). If the sample isn't in the fetched list,
   * it is fetched by uuid instead; an unknown/404 id no-ops silently.
   */
  private tryOpenDeepLinkedSample(): void {
    if (!this.pendingDeepLinkSampleId) {
      return;
    }
    const id = this.pendingDeepLinkSampleId;
    this.pendingDeepLinkSampleId = null;

    // Match on uuid OR id: search results send the index doc's `id` as the
    // sampleId param, which is the sample uuid today — but the punchlist and
    // warranty-claim deep-link handlers accept both forms, and a bare-uuid
    // assumption here silently no-ops the deep link if the shapes ever drift.
    const match = this.originalDataSource.find(
      row => `${row?.uuid}` === `${id}` || `${row?.id}` === `${id}`,
    );
    if (match) {
      this.rowClicked(match);
      this.revealDeepLinkedRow(match);
      return;
    }

    this.sampleRecordsService.fetch(id)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: resp => {
          const record = resp?.data || resp;
          if (record?.uuid) {
            this.rowClicked(record);
          }
        },
        error: () => {
          // Deep-linked sample no longer exists (or is not accessible) — stay on the list.
        },
      });
  }

  /**
   * Surfaces the deep-linked row in the left list: jumps to the row's page
   * and scrolls it into view (the highlighted-row styling comes from
   * rowClicked's selectedRow). The list has no filters, so no clearing is
   * needed.
   */
  private revealDeepLinkedRow(row): void {
    // A deep link should surface the row even when a stale search hides it.
    if (this.searchForm.controls.searchText.value) {
      this.searchForm.controls.searchText.setValue('', { emitEvent: false });
      this.paginateData(true);
    }
    const index = this.filteredRecords.indexOf(row);
    if (index < 0) {
      return;
    }
    this.currentPage = Math.floor(index / this.itemsPerPage) + 1;
    this.paginateData();
    setTimeout(() => {
      document.querySelector('app-sample-records-list .highlighted-row')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  rowClicked(row: any): void {
    this.selectedRow = row;
    this.projectsService.openRightPanel.next({
      component: CreateOrEditSampleRecordComponent,
      data: { ...row },
    });
    this.router.navigate([], {
      queryParams: {
        subTab: 'details',
        sampleId: row.uuid,
      },
      queryParamsHandling: 'merge',
    });
  }

  create(): void {
    this.router.navigate([], {
      queryParams: { sampleId: null },
      queryParamsHandling: 'merge',
    });
    this.projectsService.openRightPanel.next({
      component: CreateOrEditSampleRecordComponent,
      data: {},
    });
  }

  onPageChanged(page: number): void {
    this.projectsService.closeRightPanel.next();
    this.currentPage = page;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) this.currentPage = 1;
    const needle = (this.searchForm.controls.searchText.value || '').trim().toLowerCase();
    this.filteredRecords = this.originalDataSource.filter(row => this.matchesSearch(row, needle));
    this.totalRecords = this.filteredRecords.length;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredRecords.length);
    this.dataSource = this.filteredRecords.slice(startIndex, endIndex);
  }

  formatInProjectTimezone(timestamp: string, format: string): string {
    if (!timestamp) return '';
    const tz = this.projectTimezone || 'UTC';
    return moment.utc(timestamp).tz(tz).format(format);
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
